"use client";

import { IconArrowNarrowRight, IconInfoCircle } from "@tabler/icons-react";
import { useLocale, useTranslations } from "next-intl";
import Briefcase from "@/assets/icons/briefcase.svg";
import { ButtonLoadingIcon } from "@/components/common/buttons/button-loading-icon";
import { TooltipWrapper } from "@/components/tooltip-wrapper";
import { Button } from "@/components/ui/button";
import { RemoveDiscount } from "@/features/cart/components/discount/remove-discount";
import { useGoToCheckout } from "@/features/cart/hooks/use-go-to-checkout";
import { getDeliveryDate } from "@/lib/helpers/diet-dates";
import { useDiets } from "@/lib/hooks/use-diets";
import { getLocalePrice } from "@/lib/utilities";
import { DeliveryKV, DiscountsKVParsed } from "../../helpers";
import { getCartTotalPricing } from "../../helpers/cart-total-pricing";
import { useCartStore } from "../cart-provider";
import { DeliveryCost } from "../delivery-cost";
import { AddDiscount } from "../discount/add-discount";
import { type KeyValuePairProps, PriceElement } from "../pricing/price-element";

export const CartPricing = () => {
  const t = useTranslations();
  const cartStore = useCartStore((state) => state);
  const { activeItems, goToCheckout, isLeaving } = useGoToCheckout();
  const cost = cartStore.getDeliveryCost();
  const emptyDates = activeItems.some(
    (item) => item.configurationItem.dates.length === 0,
  );
  const locale = useLocale();

  const discountsAutomatic: KeyValuePairProps[] | null = DiscountsKVParsed({
    discounts: cartStore.pricing?.discounts,
    t,
    total: true,
    type: ["automatic", "diet_length"],
    label: t("cart.configuration.discount.total"),
  });

  const discountsManual: KeyValuePairProps[] | null = DiscountsKVParsed({
    discounts: cartStore.pricing?.discounts,
    t,
    total: true,
    label: "codeName",
    type: ["manual"],
  });

  const deliveryPrice: KeyValuePairProps[] = DeliveryKV({
    pricing: cartStore.pricing?.delivery,
    t,
  });

  // Discounts and delivery live in `pricing`; the amounts to pay come from the
  // total that also covers the Foodango products (MPL-3829).
  const totalPricing = getCartTotalPricing(cartStore);
  const totalPrice = totalPricing?.final.gross;

  const disabled = emptyDates || activeItems.length === 0 || isLeaving;

  const { data: diets } = useDiets({
    filters: { diets: cartStore.getDietIds() },
    callback: cartStore.setDeliveryCosts,
  });

  const availableCodes =
    diets?.["hydra:member"].filter(
      (diet) => diet.discountCodes && diet.discountCodes?.length > 0,
    ) || [];

  const selectedDates = cartStore.items.flatMap((item) =>
    item.configurationItem.dates.map((date) => new Date(date)),
  );
  const formattedDeliveryDate = getDeliveryDate(selectedDates, locale);

  const tooltipDeliveryInfo = () => (
    <span className="font-medium text-sm">
      {t("cart.configuration.deliveryTooltipWithoutHours", {
        deliveryDate: formattedDeliveryDate,
      })}
    </span>
  );

  // TODO add discoundCodes for multidiet

  return (
    <PriceElement
      content={{
        header: null,
        kvs: [
          {
            label: t("cart.pricing.orderPrice"),
            value: getLocalePrice({
              price: totalPricing?.before.gross ?? 0,
            }),
            className: "font-normal",
            valueClassName: "font-bold",
          },
          ...deliveryPrice,
          ...(discountsAutomatic || []),
          ...(discountsManual || []),
        ],
        footer: cartStore.discountCode ? (
          <RemoveDiscount className="-mt-1" />
        ) : null,
      }}
      footer={{
        header: <AddDiscount availableCodes={availableCodes.length} />,
        className: "px-4 pt-4",
        kvs: [
          {
            label: t("cart.pricing.totalPrice"),
            value: getLocalePrice({
              price: totalPrice ?? 0,
            }),
            className: "font-bold mt-3",
            valueClassName: "text-lg text-brand-violet",
          },
        ],
        buttons: (
          <Button
            className="mt-3 h-14 w-full space-x-2"
            data-pw="to-checkout-button"
            disabled={disabled}
            form="configure-diet-form"
            onClick={goToCheckout}
            type="submit"
            variant="secondary"
          >
            <div className="flex items-center gap-1.5">
              <span>{t("cart.pricing.cta")}</span>
              <ButtonLoadingIcon
                className="size-4"
                icon={<IconArrowNarrowRight stroke={1.25} />}
                loading={isLeaving}
              />
            </div>
          </Button>
        ),
        other: [
          cost && (
            <DeliveryCost
              {...cost}
              calculated={!!cartStore.pricing?.delivery}
              hideNotFree
              key="delivery"
              className="m-0!"
            />
          ),
          locale === "cz" && (
            <div
              className="mt-1 flex items-center justify-end gap-1"
              key="delivery-info"
            >
              <p className="font-medium">
                {t("cart.configuration.deliveryInfo")}
              </p>
              <TooltipWrapper
                align="end"
                className="w-62 px-4 py-3"
                content={tooltipDeliveryInfo()}
                sideOffset={10}
              >
                <IconInfoCircle className="size-4" />
              </TooltipWrapper>
            </div>
          ),
        ],
      }}
      header={{
        icon: <Briefcase className="h-6 w-6" />,
        label: t("cart.pricing.header"),
      }}
    />
  );
};
